/**
 * Graphics surface protocol (SPEC §9.1): the browser sources in OBS connect to
 * `ws://127.0.0.1:4700/graphics/ws`. Surfaces are **read-only consumers**: they may only
 * say `hello` (which surface they are), `ready` (first state rendered: the render-ready
 * handshake preflight checks) and `notReady` (a bundle font failed to load). Anything else
 * closes the socket.
 *
 * `ready` does not wait for fonts: OBS keeps browser sources on scenes no view shows
 * hidden, and a hidden page never finishes font loading (seen on OBS 32.2.2). Fonts load
 * in the background; a failure sends `notReady`, so preflight still catches it.
 *
 * The server sends a full {@link SurfaceStateMessage} on hello and whenever that
 * surface's projection changes. Projections are small (one scene's frames, or the
 * overlay's graphics), so there are no patches. `serverTime` lets timers use a target
 * time plus the server clock offset, so they never drift (SPEC §9.4).
 */
import { z } from 'zod';
import { BundleTokensSchema, DensitySchema, FontUseSchema } from './bundle.ts';
import { PaletteColorsSchema, RendererSchema } from './palette.ts';

export const SURFACE_WS_PATH = '/graphics/ws';

/** `lab` is the Motion Lab (`/graphics/lab`): not in the fixture, never counted by health or preflight. */
export const SurfaceKindSchema = z.enum(['background', 'cameras', 'overlay', 'scene', 'lab']);
export type SurfaceKind = z.infer<typeof SurfaceKindSchema>;

export const SurfaceHelloSchema = z.strictObject({
  type: z.literal('hello'),
  surface: SurfaceKindSchema,
  /** Scene name for `cameras` (`?scene=`) and `scene` (`/graphics/scene/<name>`); `null` otherwise. */
  scene: z.string().max(256).nullable(),
});
export type SurfaceHello = z.infer<typeof SurfaceHelloSchema>;

export const SurfaceReadySchema = z.strictObject({
  type: z.literal('ready'),
  /** Bundle the surface rendered with (preflight compares it to the active one). */
  bundleId: z.string().max(64),
});

/** A bundle font failed to load: the surface would draw with a fallback font. */
export const SurfaceNotReadySchema = z.strictObject({
  type: z.literal('notReady'),
  reason: z.string().max(200),
});

export const SurfaceClientMessageSchema = z.discriminatedUnion('type', [SurfaceHelloSchema, SurfaceReadySchema, SurfaceNotReadySchema]);
export type SurfaceClientMessage = z.infer<typeof SurfaceClientMessageSchema>;

const rect = z.strictObject({ x: z.number(), y: z.number(), width: z.number(), height: z.number() });

/** The on-air text for one person (nameplate, lower third). */
export const PersonLiteSchema = z.strictObject({
  participantId: z.string(),
  displayName: z.string(),
  jobTitle: z.string(),
  organization: z.string(),
});
export type PersonLite = z.infer<typeof PersonLiteSchema>;

export const SessionLiteSchema = z.strictObject({
  sessionId: z.string(),
  title: z.string(),
  type: z.enum(['session', 'workshop', 'break']),
  day: z.string(),
  startTime: z.string(),
  endTime: z.string(),
  track: z.string(),
  theme: z.string(),
  /** `startTime` on `day` as an absolute time (graphics machine's timezone), or `null` if unparseable. */
  startsAt: z.string().nullable(),
  endsAt: z.string().nullable(),
  participants: z.array(PersonLiteSchema).max(6),
});
export type SessionLite = z.infer<typeof SessionLiteSchema>;

/** One camera frame as OBS draws the video: the shared geometry module's `contentBounds`. */
export const FrameSchema = z.strictObject({
  slotId: z.string(),
  cameraId: z.string(),
  /** Axis-aligned bounds of the visible video on the canvas. */
  rect,
  /** The visible video relative to `position`, before rotation (draw it like OBS does). */
  local: rect,
  position: z.strictObject({ x: z.number(), y: z.number() }),
  rotation: z.number(),
  /** Where the nameplate goes, or `null` (bundle default inside the frame). */
  nameplate: z
    .strictObject({ x: z.number(), y: z.number(), horizontal: z.enum(['left', 'center']), vertical: z.enum(['top', 'bottom']) })
    .nullable(),
  /** Who is nameplated (the camera's participant), or `null` for an unassigned camera. */
  person: PersonLiteSchema.nullable(),
});
export type Frame = z.infer<typeof FrameSchema>;

export const SurfaceStateSchema = z.strictObject({
  bundle: z.strictObject({
    bundleId: z.string(),
    /** Which renderer draws it (ADR 0002); equals `tokens.renderer`. */
    renderer: RendererSchema,
    tokens: BundleTokensSchema,
    /** The palette to draw with: bundle default overlaid with the project override (role → colour). */
    palette: PaletteColorsSchema,
    /** Absolute URLs (`/bundles/<id>/…`) of the bundle's assets and fonts. */
    assets: z.record(z.string(), z.string()),
    /** Absolute URL of the asset library JSON, or `null`. */
    library: z.string().nullable(),
    /** Each font with the code points it can draw (inclusive ranges), for the coverage sanitiser. */
    fonts: z.array(
      z.strictObject({
        family: z.string(),
        url: z.string(),
        weight: z.number(),
        style: z.enum(['normal', 'italic']),
        use: FontUseSchema,
        coverage: z.array(z.tuple([z.number().int(), z.number().int()])).max(4096),
      }),
    ),
    /** Sponsor box rotation, in order (absolute URLs). */
    sponsors: z.array(z.strictObject({ name: z.string(), url: z.string() })).min(1).max(12),
  }),
  /** How much the procedural environment does (`signal`); surfaces ramp to a new value. */
  density: DensitySchema,
  session: SessionLiteSchema.nullable(),
  nextSession: SessionLiteSchema.nullable(),
  /** `cameras` surface: frames of its scene (empty for other surfaces or unknown scenes). */
  frames: z.array(FrameSchema).max(16),
  /** `overlay` surface. */
  overlay: z.strictObject({
    sponsorBox: z.boolean(),
    logoBug: z.boolean(),
    timer: z.boolean(),
    lowerThird: PersonLiteSchema.nullable(),
  }),
  /** `scene` surfaces: which contract component fills the scene. */
  sceneComponent: z.enum(['startingSoon', 'break', 'technicalDifficulties', 'nextSession', 'endScreen', 'fullScreenMessage']).nullable(),
});
export type SurfaceState = z.infer<typeof SurfaceStateSchema>;

export const SurfaceStateMessageSchema = z.strictObject({
  type: z.literal('surfaceState'),
  serverTime: z.iso.datetime({ offset: true }),
  state: SurfaceStateSchema,
});
export type SurfaceStateMessage = z.infer<typeof SurfaceStateMessageSchema>;

/** WebSocket close codes for surfaces. */
export const SurfaceCloseCode = {
  /** The surface sent something other than hello/ready: surfaces are read-only. */
  ReadOnly: 4405,
  ServerShutdown: 4503,
} as const;
